import React from 'react';
import PropTypes from 'prop-types';
import LazyLoad from 'react-lazyload';
import classNames from 'classnames';

import styles from './style.scss';


const YOUTUBE_API_URL = 'https://www.youtube.com/iframe_api';
let loadedYT;

const EmptyYouTubePlayer = () => (
  <div className={classNames(styles.youtubePlayer, styles.isEmpty, 'jest-youtube-player-empty')}>
    <div className={styles.youtubePlayerEmpty} />
  </div>
);

class YouTubePlayer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showPlayer: false,
      error: false,
    };
    this.player = null;
    this.container = null;
  }

  onPlayerError(event) {
    this.setState({ error: true });
    Raven.captureException(event, { extra: event.data });
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error: true });
    Raven.captureException(error, { extra: errorInfo });
  }

  onPlayerReady(event) {
    const { volume } = this.props;
    event.target.setVolume(volume);
  }

  onPlayerStateChange(event) {
    const { name } = this.props;
    // start video
    if (event.data === YT.PlayerState.PLAYING) {
      yaMetrikaReachGoal('StartVideo');
      ga('send', 'event', {
        eventCategory: 'Content',
        eventAction: 'StartVideo',
        eventLabel: name,
      });
    }
  }

  onYouTubeIframeAPIReady() {
    const { videoId, timeLineColor, startWithSecond, blockId, isMobile } = this.props;
    if (videoId) {
      let player = new YT.Player(`player-${videoId}-${blockId}`, {
        height: isMobile ? '240' : '320',
        width: '768',
        videoId,
        events: {
          onReady: this.onPlayerReady.bind(this),
          onStateChange: this.onPlayerStateChange.bind(this),
          onError: this.onPlayerError.bind(this),
        },
        playerVars: {
          autoplay: 1,
          start: startWithSecond,
          color: timeLineColor,
          controls: 2,
          modestbranding: 1,
          showinfo: 0,
          rel: 0,
        },
      });
      this.player = player;
    }
  }

  componentDidMount() {
    const { showPlayer } = this.state;
    if (showPlayer) {
      this.handleAddPlayerScripts();
    }
  }

  componentWillUnmount() {
    this.container = null;
  }

  refContainer(container) {
    this.container = container;
  }

  handlePlayVideo() {
    this.setState({ showPlayer: true });
    this.handleAddPlayerScripts();
  }

  handleAddPlayerScripts() {
    let isAddedScript = false;
    [...document.scripts].map((item) => {
      if (item.src === YOUTUBE_API_URL) {
        isAddedScript = true;
      }
    });

    if (!loadedYT && !isAddedScript) {
      loadedYT = new Promise((resolve) => {
        const tag = document.createElement('script');
        tag.async = true;
        tag.src = YOUTUBE_API_URL;
        const firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
        window.onYouTubeIframeAPIReady = () => resolve(window.YT);
      });
    }

    loadedYT.then(() => {
      this.onYouTubeIframeAPIReady();
    });
  }

  render() {
    const { videoId, name, blockId } = this.props;
    const { showPlayer, error } = this.state;

    if (!videoId || error) {
      return (<EmptyYouTubePlayer />);
    }
    const imageUrlS = `https://img.youtube.com/vi/${videoId}/0.jpg`;
    const imageUrlM = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    const imgeUrlL = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;

    return (
      <div className={classNames(styles.youtubePlayer, 'jest-youtube-player')}>
        {(!showPlayer && videoId) && (
          <div className={classNames(styles.previewContainer, 'jest-youtube-player-preview')}
            onClick={() => { this.handlePlayVideo(); }}>
            <LazyLoad offset={200} height={360} className="jest-youtube-player-preview-image">
              <picture className={styles.previewImage}>
                <source srcSet={imageUrlS} media="min-width: 414px" />
                <source srcSet={imageUrlM} media="min-width: 767px" />
                <img className={styles.image} src={imgeUrlL} alt={name} />
              </picture>
              <div className={classNames(styles.playButton, 'jest-youtube-palyer-preview-button-play')} />
            </LazyLoad>
          </div>
        )}
        {(showPlayer && videoId) && (
          <div id={`player-${videoId}-${blockId}`}
            className={classNames(styles.playerContainer, 'jest-youtube-player-container')}
            ref={(ref) => { this.refContainer(ref); }} />
        )}
      </div>
    );
  }
};

YouTubePlayer.propTypes = {
  videoId: PropTypes.string,
  blockId: PropTypes.string,
  name: PropTypes.string,
  timeLineColor: PropTypes.oneOf(['red', 'white']),
  startWithSecond: PropTypes.number,
  volume: (props, propName) => {
    if (props[propName] < 0 || props[propName] > 100) {
      return new Error(
        `Invalid "volume" value - ${props[propName]} in YouTubePlayer Component`
      );
    }
  },
  isMobile: PropTypes.bool,
};

YouTubePlayer.defaultProps ={
  isMobile: false,
  timeLineColor: 'red',
  startWithSecond: 0,
  volume: 50,
  name: '',
};

export default YouTubePlayer;
